
"use client";
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  Fade,
  Slide,
  Tooltip,
  Badge,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import MinimizeIcon from "@mui/icons-material/Minimize";
import CloseIcon from "@mui/icons-material/Close";
import { keyframes } from "@mui/system";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// Pulse animation for bot avatar
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(118, 53, 221, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(118, 53, 221, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(118, 53, 221, 0);
  }
`;

// Typing indicator animation
const typing = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

const TypingIndicator = () => (
  <Box sx={{ display: "flex", gap: 0.5, padding: 1 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "#1976d2",
          animation: `${typing} 1.4s infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
  </Box>
);

const initialMessage = {
  id: 1,
  text: "Hi! How can I help you today?",
  sender: "bot",
  timestamp: new Date(),
};


export default function ChatbotUI() {
  const [messages, setMessages] = useState([
    initialMessage
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        scrollToBottom("auto"); // no animation on open
      }, 100); // wait for Slide + DOM render

      return () => clearTimeout(timer);
    }
  }, [isOpen]);


  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // API Configuration
  const API_URL = apiEndPointsConfig.chatbotApi;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const sendMessageToAPI = async (query, signal) => {
    try {
      // Get token inside the function to ensure it's fresh
      const token = localStorage.getItem("token");

      console.log("Sending request to:", API_URL);
      console.log("Query:", query);
      console.log("Token:", token ? "Present" : "Missing");

      const response = await axios.post(
        API_URL,
        {
          query: query,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: signal,
        }
      );

      console.log("API Response:", response.data);

      // Check if the response is successful
      // if (response.data?.status?.code === 200 && response.data?.data?.ok) {
      //   return {
      //     success: true,
      //     message: response.data.data.response,
      //     intent: response.data.data.intent,
      //   };
      // } else {
      //   console.error("API returned non-200 status:", response.data);
      //   return {
      //     success: false,
      //     message: response.data?.data?.response || "Sorry, I couldn't process your request. Please try again.",
      //   };
      // }
      if (
        response.data?.status?.code === 200 &&
        response.data?.error === false &&
        response.data?.data?.rawResponse?.ok
      ) {
        return {
          success: true,
          message:
            response.data.data.message ||
            response.data.data.rawResponse.answer,
          intent:
            response.data.data.rawResponse.pipeline_stats?.intent || null,
          followUps:
            response.data.data.rawResponse.followUpQuestions || [],
        };
      }

    } catch (error) {
      console.error("API Error Details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      if (axios.isCancel(error)) {
        console.log("🚫 Request cancelled:", error.message);
        return {
          success: false,
          message: null, // Don't show error message for cancelled requests
          cancelled: true,
        };
      }
      if (error.code === 'ERR_NETWORK') {
        return {
          success: false,
          message: "🌐 Cannot connect to server. Please check your connection.",
        };
      }

      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Sorry, I'm having trouble connecting right now. Please try again later.",
      };
    }
  };



  const handleSendMessage = async () => {
    if (inputValue.trim() === "") return;

    // ✅ Cancel previous request if it exists
    if (abortControllerRef.current) {
      console.log("🚫 Cancelling previous request...");
      abortControllerRef.current.abort();
    }

    // ✅ Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    const currentInput = inputValue;

    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      text: currentInput,
      sender: "user",
      timestamp: new Date(),

    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setIsTyping(true);

    try {
      // ✅ Pass the signal here
      const apiResponse = await sendMessageToAPI(
        currentInput,
        abortControllerRef.current.signal  // This is the signal
      );

      console.log("📩 API Response received:", apiResponse);

      // ✅ Don't show message if request was cancelled
      if (apiResponse.cancelled) {
        setIsTyping(false);
        setIsLoading(false);
        return;
      }

      // Add a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsTyping(false);

      if (apiResponse.message) {
        const botMessage = {
          id: `bot-${Date.now()}`,
          text: apiResponse.message,
          sender: "bot",
          timestamp: new Date(),
          intent: apiResponse.intent || null,
          followUps: apiResponse.followUps || [],

        };
        setMessages((prev) => [...prev, botMessage]);
      }

    } catch (error) {
      console.error("❌ Error in handleSendMessage:", error);

      setIsTyping(false);

      // Add error message to chat
      const errorMessage = {
        id: `error-${Date.now()}`,
        text: "Sorry, something went wrong. Please try again.",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);

    } finally {
      setIsLoading(false);
      abortControllerRef.current = null; // ✅ Clear the reference
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleClearChat = () => {
    // Cancel ongoing request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsTyping(false);
    setIsLoading(false);
    setMessages([initialMessage]);
  };

  const handleFollowUpClick = (question) => {
    setInputValue(question);
    inputRef.current?.focus();

    // 🔥 Optional: auto-send
    // handleSendMessage();
  };

  return (
    <>
      {/* Floating Chat Button */}
      {/* {!isOpen && (
        <Tooltip title="Chat with us" placement="left">
          <IconButton
            onClick={toggleChat}
            sx={{
              position: "fixed",
              bottom: 30,
              right: 30,
              width: 70,
              height: 70,
              color: "white",
              boxShadow: "0 4px 20px rgba(118, 53, 221, 0.4)",
              zIndex: 9998,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "scale(1.1)",
                boxShadow: "0 6px 25px rgba(118, 53, 221, 0.5)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
              animation: "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: {
                  opacity: 0,
                  transform: "scale(0.8)",
                },
                to: {
                  opacity: 1,
                  transform: "scale(1)",
                },
              },
            }}
          >
            <img
              src="/convoze.chatbot.png"
              alt="chat"
              style={{ width: 110, height: 110 }}
            />
          </IconButton>
        </Tooltip>
      )} */}
      {/* Right-side Vertical Chat Button */}
      {!isOpen && (
        <Tooltip title="Chat with Convoze Bot" placement="left">
          <Box
            onClick={toggleChat}
            sx={{
              position: "fixed",
              top: "95%",
              right: 0,
              transform: "translateY(-50%)",
              zIndex: 9998,
              cursor: "pointer",
              px: 1.3,
              py: 3.2,
              borderTopLeftRadius: "16px",
              borderBottomLeftRadius: "16px",
              // background: "linear-gradient(135deg, #1f2937, #111827)",
              // background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
              // background: "linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%)",
              // Deep Blue → Indigo
              // background: "linear-gradient(135deg, #2563eb, #1e3a8a)",
              // Azure → Sky
              // background: "linear-gradient(135deg, #0284c7, #0ea5e9)",
              // Indigo → Cyan
              background: "linear-gradient(135deg, #4f46e5, #06b6d4)",
              boxShadow: "0 8px 28px rgba(245, 158, 11, 0.4)",

              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                right: 4,
                boxShadow: "0 12px 40px rgba(118, 53, 221, 0.5)",
              },
            }}
          >
            <SmartToyIcon sx={{ fontSize: 25, opacity: 0.9 }} />


          </Box>
        </Tooltip>
      )}



      {/* Chat Window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "70vh",
            width: "400px",
            maxWidth: "90vw",
            bgcolor: "#ffffff",
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 9999,
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              boxShadow: "0 15px 50px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          {/* Header with Gradient */}
          <Paper
            elevation={0}
            sx={{
              padding: 2.5,
              background: "linear-gradient(135deg, #7635dd 0%, #9b59b6 100%)",
              color: "white",
              borderRadius: 0,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#44b700",
                      color: "#44b700",
                      boxShadow: "0 0 0 2px white",
                      "&::after": {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        animation: `${pulse} 1.2s infinite`,
                        border: "1px solid currentColor",
                        content: '""',
                      },
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      width: 45,
                      height: 45,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </Badge>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    sx={{ lineHeight: 1.2 }}
                  >
                    Convoze Bot
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Always here to help
                  </Typography>
                </Box>
              </Stack>
              {/* <Stack direction="row" spacing={0.5}> */}
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Clear chat">
                    <IconButton
                      size="small"
                      onClick={handleClearChat}
                      sx={{
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.15)" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Close">
                    <IconButton
                      size="small"
                      onClick={toggleChat}
                      sx={{
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {/* <Tooltip title="Close">
                  <IconButton
                    size="small"
                    onClick={toggleChat}
                    sx={{
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack> */}
            </Stack>
          </Paper>

          {/* Messages Container with Custom Scrollbar */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              bgcolor: "#f8f9fa",
              backgroundImage: `
              radial-gradient(circle at 10% 20%, rgba(118, 53, 221, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 90% 80%, rgba(118, 53, 221, 0.03) 0%, transparent 50%)
            `,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#d0d0d0",
                borderRadius: "10px",
                "&:hover": {
                  background: "#b0b0b0",
                },
              },
            }}
          >
            {messages.map((message, index) => (
              <Fade in={true} timeout={500} key={message.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.sender === "user" ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 1.5,
                    animation: "slideIn 0.3s ease-out",
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform:
                          message.sender === "user"
                            ? "translateX(20px)"
                            : "translateX(-20px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                    // bgcolor:'orange'
                  }}
                >
                  {message.sender === "bot" && (
                    <Avatar
                      sx={{
                        bgcolor:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        width: 36,
                        height: 36,
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}

                  <Box sx={{}}>
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: "75%",
                        padding: "10px 16px",
                        background:
                          message.sender === "user"
                            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            : "#ffffff",
                        color: message.sender === "user" ? "white" : "#2d3748",
                        borderRadius: "18px",
                        borderBottomLeftRadius:
                          message.sender === "bot" ? "4px" : "18px",
                        borderBottomRightRadius:
                          message.sender === "user" ? "4px" : "18px",
                        boxShadow:
                          message.sender === "user"
                            ? "0 4px 15px rgba(102, 126, 234, 0.4)"
                            : "0 2px 10px rgba(0, 0, 0, 0.08)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow:
                            message.sender === "user"
                              ? "0 6px 20px rgba(102, 126, 234, 0.5)"
                              : "0 4px 15px rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.5,
                          // wordBreak: "break-word",
                          fontSize: "0.95rem",
                          p: "1 0 1 1"
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...props }) => (
                              <Typography
                                variant="body2"
                                sx={{ lineHeight: 1.5, fontSize: "0.95rem", mb: 1 }}
                                {...props}
                              />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul style={{ paddingLeft: "1.2rem", marginTop: 4, marginBottom: 4 }} {...props} />
                            ),
                            li: ({ node, ...props }) => (
                              <li style={{ marginBottom: 4 }} {...props} />
                            ),
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>

                        {/* {message.text} */}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          // marginTop: "6px",
                          opacity: 0.7,
                          fontSize: "0.7rem",
                          textAlign: "right"
                        }}
                      >
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Paper>
                    {message.sender === "bot" &&
                      message.followUps?.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            // ml: 6,
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {message.followUps.map((q, idx) => (
                            <Button
                              key={idx}
                              size="small"
                              variant="outlined"
                              onClick={() => handleFollowUpClick(q)}
                              sx={{
                                borderRadius: "16px",
                                gap: 1,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                borderColor: "#7635dd",
                                color: "#7635dd",
                                "&:hover": {
                                  backgroundColor: "rgba(118,53,221,0.08)",
                                },
                              }}
                            >
                              {q}
                            </Button>
                          ))}
                        </Box>
                      )}
                  </Box>


                  {message.sender === "user" && (
                    <Avatar
                      sx={{
                        bgcolor: "#4a5568",
                        width: 36,
                        height: 36,
                        background:
                          "linear-gradient(135deg, #434343 0%, #000000 100%)",
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                </Box>
              </Fade>
            ))}

            {isTyping && (
              <Fade in={true}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      width: 36,
                      height: 36,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: "#ffffff",
                      borderRadius: "18px",
                      borderBottomLeftRadius: "4px",
                      padding: "10px 16px",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <TypingIndicator />
                  </Paper>
                </Box>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Divider */}
          <Divider sx={{ borderColor: "#e2e8f0" }} />

          {/* Enhanced Input Area */}
          <Box
            sx={{
              padding: 2.5,
              bgcolor: "white",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                size="small"
                disabled={isLoading}
                inputRef={inputRef}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "24px",
                    bgcolor: "#f7fafc",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "#edf2f7",
                    },
                    "&.Mui-focused": {
                      bgcolor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#7635dd",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.95rem",
                  },
                }}
              />
              {/* <Tooltip title="Send message" arrow>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={inputValue.trim() === "" || isLoading}
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      width: 48,
                      height: 48,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5568d3 0%, #653a8b 100%)",
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                      "&:disabled": {
                        bgcolor: "#cbd5e0",
                        color: "#a0aec0",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip> */}
              <Tooltip title={isLoading ? "Cancel" : "Send message"} arrow>
                <span>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      if (isLoading) {
                        // ✅ Cancel the request
                        if (abortControllerRef.current) {
                          console.log("🚫 User cancelled request");
                          abortControllerRef.current.abort();
                          setIsLoading(false);
                          setIsTyping(false);
                          abortControllerRef.current = null;
                        }
                      } else {
                        // Send message normally
                        handleSendMessage();
                      }
                    }}
                    disabled={!isLoading && inputValue.trim() === ""}
                    sx={{
                      background:
                        isLoading
                          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" // Red when loading
                          : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      width: 48,
                      height: 48,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background:
                          isLoading
                            ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                            : "linear-gradient(135deg, #5568d3 0%, #653a8b 100%)",
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                      "&:disabled": {
                        bgcolor: "#cbd5e0",
                        color: "#a0aec0",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CloseIcon /> // ✅ Show X icon when loading to indicate cancel
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Slide>
    </>
  );
} 